import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  industrializationService,
  type DossierCostEstimate,
  type IndustrializationDossier,
} from '../services/industrializationService';

type StepId = 'step1' | 'step2' | 'step3' | 'step4';

interface IndustrializationState {
  dossier: IndustrializationDossier | null;
  costEstimate: DossierCostEstimate | null;
  isLoading: boolean;
  isRunningStep: boolean;
  currentStep: StepId | null;
  error: string | null;
}

interface IndustrializationActions {
  loadDossier: (subProjectId: string, solutionId: string) => Promise<void>;
  estimateCost: (subProjectId: string, solutionId: string) => Promise<void>;
  runStep: (subProjectId: string, solutionId: string, step: StepId, language?: string) => Promise<void>;
  deleteDossier: (solutionId: string) => Promise<void>;
  reset: () => void;
}

const initialState: IndustrializationState = {
  dossier: null,
  costEstimate: null,
  isLoading: false,
  isRunningStep: false,
  currentStep: null,
  error: null,
};

export const useIndustrializationStore = create<IndustrializationState & IndustrializationActions>()(
  devtools(
    (set) => ({
      ...initialState,

      loadDossier: async (subProjectId, solutionId) => {
        set({ isLoading: true, error: null });
        try {
          const dossier = await industrializationService.getDossier(subProjectId, solutionId);
          set({ dossier, isLoading: false });
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : 'Failed to load dossier';
          set({ error: msg, isLoading: false });
        }
      },

      estimateCost: async (subProjectId, solutionId) => {
        set({ error: null });
        try {
          const costEstimate = await industrializationService.estimateCost(subProjectId, solutionId);
          set({ costEstimate });
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : 'Failed to estimate cost';
          set({ error: msg });
        }
      },

      runStep: async (subProjectId, solutionId, step, language = 'English') => {
        set({ isRunningStep: true, currentStep: step, error: null });
        try {
          const dossier = await industrializationService.runStep(subProjectId, solutionId, step, language);
          set({ dossier, isRunningStep: false, currentStep: null });
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : `Step ${step} failed`;
          set({ error: msg, isRunningStep: false, currentStep: null });
        }
      },

      deleteDossier: async (solutionId) => {
        set({ error: null });
        try {
          await industrializationService.deleteDossier(solutionId);
          set({ dossier: null, costEstimate: null });
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : 'Failed to delete dossier';
          set({ error: msg });
        }
      },

      reset: () => set(initialState),
    }),
    { name: 'industrialization-store' },
  ),
);
